import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useEntitlements } from "@/lib/useEntitlements";
import { SignOut, ShieldCheck, Eye, EnvelopeSimple, CreditCard, DownloadSimple, Trash } from "@phosphor-icons/react";

export default function Settings() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const { ent, usage, loading: entLoading, refresh: refreshEnt } = useEntitlements();
  const isCandidate = user?.role === "candidate";
  const [open, setOpen] = useState(!!user?.open_to_work);
  const [invites, setInvites] = useState([]);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    if (isCandidate) {
      api.get("/candidate/invitations").then((r) => setInvites(r.data.invitations)).catch(() => {});
      api.get("/documents").then((r) => setDocs(r.data.documents)).catch(() => {});
    }
  }, [isCandidate]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const cancelTrial = async () => {
    try { await api.post("/billing/cancel"); await refreshEnt();
      toast.success("Cancelled. Your data is kept; you can reactivate anytime."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleVisibility = async (v) => {
    setOpen(v);
    try { await api.put("/candidate/visibility", { open_to_work: v }); setUser({ ...user, open_to_work: v });
      toast.success(v ? "You're now discoverable to employers & recruiters" : "You're no longer discoverable"); }
    catch (e) { setOpen(!v); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Your account, privacy and access level." />
      <div className="max-w-xl space-y-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="account-card">
          <h2 className="font-heading font-bold text-lg mb-4">Account</h2>
          <dl className="space-y-3 text-sm">
            <Row label="Name" value={user?.name} />
            <Row label="Email" value={user?.email} />
            <Row label="Role" value={user?.role} />
            <Row label="Sign-in" value={user?.auth_provider === "google" ? "Google" : "Email & password"} />
          </dl>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="plan-card">
          <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><CreditCard weight="duotone" /> Plan & usage</h2>
          {entLoading ? <p className="text-sm text-slate-400">Loading…</p> : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900" data-testid="plan-label">{ent.plan_label}</p>
                  <p className="text-xs text-slate-500 capitalize" data-testid="plan-status">Status: {ent.subscription_status}
                    {ent.trial_ends_at && ent.subscription_status === "trial" ? ` · trial ends ${new Date(ent.trial_ends_at).toLocaleDateString()}` : ""}</p>
                </div>
                <Link to="/pricing"><Button variant="outline" size="sm" className="border-slate-300" data-testid="view-plans-btn">View plans</Button></Link>
              </div>
              <dl className="mt-4 space-y-2 text-sm border-t border-slate-100 pt-3">
                {isCandidate && <Row label="Criteria profiles" value={`${usage.criteria_profiles ?? 0} / ${fmtLimit(ent.criteria_profiles_limit)}`} />}
                {user?.role === "employer" && <Row label="Active roles" value={`${usage.active_roles ?? 0} / ${fmtLimit(ent.max_active_roles)}`} />}
                <Row label="Talent Reach" value={ent.can_use_talent_reach ? (ent.talent_reach_level === "advanced" ? "Advanced" : "Basic") : "—"} />
                {isCandidate && <Row label="Application pack tools" value={ent.can_generate_application_pack ? "Included" : "—"} />}
              </dl>
              {["trial", "active"].includes(ent.subscription_status) && ent.plan !== "candidate_free" && (
                <Button variant="outline" size="sm" onClick={cancelTrial} data-testid="cancel-plan-btn" className="mt-4 border-slate-300 text-slate-700">
                  {ent.subscription_status === "trial" ? "Cancel trial" : "Cancel plan"}
                </Button>
              )}
            </>
          )}
        </div>

        {isCandidate && (
          <>
            <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="visibility-card">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Eye weight="duotone" /> Discoverability</h2>
              <label className="mt-4 flex items-center justify-between gap-3 cursor-pointer">
                <span className="text-sm text-slate-700 max-w-sm">Open to work — let employers & recruiters find you in matching. They see only a limited professional profile; never your documents or contact details until you accept an invitation.</span>
                <Switch checked={open} onCheckedChange={toggleVisibility} data-testid="open-to-work-toggle" />
              </label>
              <p className="mt-3 text-xs text-slate-500">Default is off. Discoverability never grants access to your CVs, certificates or contact — those are shared only when you explicitly accept.</p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="invitations-card">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2"><EnvelopeSimple weight="duotone" /> Invitations</h2>
              {invites.length === 0 && <p className="mt-3 text-sm text-slate-500">No invitations yet.</p>}
              <div className="mt-3 space-y-3">
                {invites.map((inv) => (
                  <InviteRow key={inv.id} inv={inv} docs={docs} onDone={() =>
                    api.get("/candidate/invitations").then((r) => setInvites(r.data.invitations))} />
                ))}
              </div>
            </div>
          </>
        )}

        <PrivacyDataCard role={user?.role} authProvider={user?.auth_provider} />

        <Button variant="outline" onClick={doLogout} data-testid="settings-logout-btn" className="border-slate-300 gap-2 text-slate-700"><SignOut /> Sign out</Button>
      </div>
    </div>
  );
}

function InviteRow({ inv, docs, onDone }) {
  const [shareContact, setShareContact] = useState(true);
  const [cvDoc, setCvDoc] = useState("none");
  const pending = inv.status === "pending";
  const respond = async (action) => {
    try {
      await api.post(`/candidate/invitations/${inv.id}/respond`, {
        action, share_contact: shareContact, cv_document_id: cvDoc === "none" ? null : cvDoc });
      toast.success(action === "accept" ? "Accepted — selected details shared" : "Declined");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-md border border-slate-200 p-4" data-testid={`invite-${inv.id}`}>
      <p className="text-sm text-slate-800"><strong>{inv.org_name}</strong> — {inv.type === "invite_to_apply" ? "invites you to apply" : "requests contact"}{inv.job_title ? ` for ${inv.job_title}` : ""}.</p>
      {pending ? (
        <div className="mt-3 space-y-2">
          <label className="flex items-center justify-between text-sm"><span>Share my contact email</span><Switch checked={shareContact} onCheckedChange={setShareContact} data-testid={`share-contact-${inv.id}`} /></label>
          <div>
            <p className="text-xs text-slate-500 mb-1">Attach a CV (optional)</p>
            <Select value={cvDoc} onValueChange={setCvDoc}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={() => respond("accept")} data-testid={`accept-${inv.id}`} className="bg-slate-900 hover:bg-slate-800">Accept</Button>
            <Button size="sm" variant="outline" onClick={() => respond("decline")} data-testid={`decline-${inv.id}`} className="border-slate-300">Decline</Button>
          </div>
        </div>
      ) : (
        <Badge variant="secondary" className="mt-2">{inv.status}</Badge>
      )}
    </div>
  );
}

function fmtLimit(v) {
  return v === -1 ? "∞" : v;
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-900 capitalize">{value || "—"}</dd>
    </div>
  );
}

function PrivacyDataCard({ role, authProvider }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    try {
      const res = await api.get("/me/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "eligvia-my-data.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data export has downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/me", { data: authProvider === "password" ? { password } : { confirm: "DELETE" } });
      toast.success("Your account and personal data have been deleted");
      await logout();
      navigate("/");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="privacy-data-card">
      <h2 className="font-heading font-bold text-lg mb-2 flex items-center gap-2"><ShieldCheck weight="duotone" /> Privacy &amp; your data</h2>
      <p className="text-sm text-slate-600">Your data is private by default. Discoverability, contact sharing and document sharing are separate, explicit consents.</p>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" onClick={exportData} data-testid="export-data-btn" className="border-slate-300 gap-2 text-slate-700"><DownloadSimple /> Download my data</Button>
        {role === "candidate" && !confirming && (
          <Button variant="outline" onClick={() => setConfirming(true)} data-testid="delete-account-btn" className="border-red-200 gap-2 text-red-600 hover:bg-red-50"><Trash /> Delete my account</Button>
        )}
      </div>

      {role !== "candidate" && (
        <p className="mt-3 text-xs text-slate-400">Employer/recruiter account deletion is handled manually because it may affect shared organisation and job data — contact support.</p>
      )}

      {confirming && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4" data-testid="delete-confirm">
          <p className="text-sm text-red-800 font-medium">This permanently deletes your account and personal data (profile, saved jobs, searches, documents, applications). This cannot be undone.</p>
          {authProvider === "password" && (
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password to confirm" data-testid="delete-password-input" className="mt-3 bg-white" />
          )}
          <div className="mt-3 flex gap-2">
            <Button onClick={deleteAccount} disabled={busy || (authProvider === "password" && !password)} data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700">{busy ? "Deleting…" : "Permanently delete"}</Button>
            <Button variant="outline" onClick={() => { setConfirming(false); setPassword(""); }} data-testid="cancel-delete-btn" className="border-slate-300">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
